import { useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  SearchOutlined,
  SyncOutlined,
  TableOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { Bento, DataTable, MenuThaoTac, StatTile, Tile, TileBody, TileHeader } from '../../../../components/shared';
import { Badge, Button } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import type { Finding, ReplicaTableState, SyncOutcome, TienDoDongBo } from '../../types';
import { bienTheLanChay, MUC_CHAC } from '../../constants-kho';
import { khoaDeXuat, mucChac, NHOM_DE_XUAT } from '../../nghiep-vu/de-xuat';
import { useDongBo, useHoSoNguon, useTrangThaiNguon, useXoaBang } from '../../hooks/useKho';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { HopXacNhan } from '../chung/HopXacNhan';

/** Dải tiến độ trên bảng trạng thái. Chu kỳ đã xong tự nói qua cột "Lần chạy gần nhất", nên không dựng gì. */
function DaiTienDo({ tienDo }: { tienDo: TienDoDongBo | null }) {
  const { t, locale } = useT();
  if (!tienDo || (!tienDo.dangChay && !tienDo.biNgat)) return null;
  const gio = (iso: string) => ngayGioVN(iso);
  const chung = {
    bang: tienDo.bang ?? '',
    thuTu: soVN(tienDo.thuTu, locale),
    soBang: soVN(tienDo.soBang, locale),
    batDau: gio(tienDo.batDau),
  };
  if (tienDo.biNgat) {
    return (
      <p className="mx-4 mb-3 rounded bg-danger-50 px-3 py-2 text-sm text-danger-700" role="alert">
        {t('troLyAiKhoSync.tienDoBiNgat', { ...chung, capNhat: gio(tienDo.capNhat) })}
      </p>
    );
  }
  return (
    <p className="mx-4 mb-3 rounded bg-sky-50 px-3 py-2 text-sm tabular-nums text-sky-700" aria-live="polite">
      {tienDo.bang
        ? t('troLyAiKhoSync.tienDoDangChay', { ...chung, daDoc: soVN(tienDo.daDoc, locale) })
        : tienDo.dangDoHoSo
          ? t('troLyAiKhoSync.tienDoDoHoSo', chung)
          : t('troLyAiKhoSync.tienDoChuanBi', chung)}
    </p>
  );
}

/**
 * Tab "Kết nối & đồng bộ" (port `NguonSyncTab.tsx` native): bốn ô số liệu, bảng trạng thái kho, kết quả chu kỳ vừa chạy và
 * đề xuất của "Dò mã & quan hệ".
 *
 * Hai hành động trên dòng (đồng bộ lẻ một bảng, xoá bảng) gom vào `MenuThaoTac` (ADR-UI-0005) thay cho hai cột nút icon.
 */
export function NguonSyncTab({
  sourceId,
  laDsn,
  trucTiep,
  outcomes,
  findings,
  daGhi,
  onGhi,
  dangGhi,
  xoaDuoc,
  dongBoDuoc,
  onDongBoBatDau,
}: {
  sourceId: string;
  laDsn: boolean;
  /** Nguồn `live`: không có bản sao — không có số dòng đã chép, mốc hay đồng bộ. */
  trucTiep: boolean;
  outcomes: SyncOutcome[] | null;
  findings: Finding[] | null;
  daGhi: Set<string>;
  onGhi: (f: Finding) => void;
  dangGhi: Finding | null;
  /** Quyền XOÁ trên module. Thiếu thì mục xoá **ẩn**, không hiện mờ. */
  xoaDuoc: boolean;
  /** Quyền SỬA nguồn — đồng bộ lẻ một bảng. */
  dongBoDuoc: boolean;
  /** Báo màn cha vừa có chu kỳ được bấm chạy, để nó mở cửa sổ hỏi lại trạng thái. */
  onDongBoBatDau: () => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const status = useTrangThaiNguon(sourceId);
  // Nguồn trực tiếp: số liệu lấy từ lần đo thống kê gần nhất — bảng lớn cỡ nào, bao nhiêu cột đo được, đo trên mẫu hay đủ.
  const hoSo = useHoSoNguon(sourceId, trucTiep);
  const xoa = useXoaBang(sourceId);
  const dongBoBang = useDongBo(sourceId);

  const hoSoTheoBang = useMemo(() => {
    const m = new Map<string, { soDong: number; trenMau: boolean; soCot: number }>();
    for (const x of hoSo.data?.tables ?? []) {
      const r = x as { table?: string; rowCount?: number; sampledFrom?: number | null; columns?: unknown[] };
      if (!r.table) continue;
      m.set(r.table, {
        soDong: r.sampledFrom ?? r.rowCount ?? 0,
        trenMau: Boolean(r.sampledFrom),
        soCot: r.columns?.length ?? 0,
      });
    }
    return m;
  }, [hoSo.data]);

  /** `ok` · `stopped` · `skipped`; mã lạ đi thẳng ra màn hình chứ không thành ô trống. */
  const nhanTrangThai = (ma: string) =>
    ma === 'ok'
      ? t('troLyAiKhoSync.trangThaiOk')
      : ma === 'stopped'
        ? t('troLyAiKhoSync.trangThaiDung')
        : ma === 'skipped'
          ? t('troLyAiKhoSync.trangThaiBoQua')
          : ma || '—';

  const [xoaBang, setXoaBang] = useState<string | null>(null);

  const tienDo = status.data?.dongBo ?? null;
  const bangDangChay = tienDo?.dangChay ? tienDo.bang : null;
  // Khoá đồng bộ lẻ khi nguồn đang chạy dở: máy chủ khoá theo NGUỒN và trả 409 cho lời gọi thứ hai.
  const dangCoChuKy = Boolean(tienDo?.dangChay);

  /**
   * Đồng bộ LẺ một bảng: một bảng dừng giữa ba mươi bảng lành không đáng chạy lại cả chu kỳ — lượt chép đầy đủ nguồn SAP
   * B1 mất gần nửa tiếng.
   */
  const chayBang = (bang: string) =>
    dongBoBang.mutate([bang], {
      onSuccess: () => {
        toast.success(t('troLyAiKhoSync.dongBoBangBatDau', { bang }));
        onDongBoBatDau();
      },
      onError: (e) => toast.error(t('troLyAiKhoSync.khongDongBoDuocBang', { loi: moTaLoi(e) })),
    });

  const xacNhanXoa = (bang: string) =>
    xoa.mutate(bang, {
      onSuccess: (r) => {
        toast.success(t('troLyAiKhoSync.daXoaBang', { bang: r.bang }));
        if (r.dinhNghiaLech.length) {
          toast.warning(
            t('troLyAiKhoSync.xoaBangLamLech', { n: soVN(r.dinhNghiaLech.length, locale), ds: r.dinhNghiaLech.join(', ') }),
          );
        }
        // Gỡ bảng là thu quyền của những vai từng được mở nó — nói ra, không thu trong im lặng.
        if (r.quyenDaGo) toast.warning(t('troLyAiKhoSync.xoaBangGoQuyen', { n: soVN(r.quyenDaGo, locale) }));
        setXoaBang(null);
      },
    });

  const cotTen: NonNullable<TableProps<ReplicaTableState>['columns']>[number] = {
    title: t('troLyAiKhoSync.colBang'),
    dataIndex: 'table',
    width: 200,
    render: (_v, r) => (
      <div className="min-w-0">
        <p className="truncate font-medium text-content-primary">{r.table}</p>
        {trucTiep ? null : (
          <p className="truncate text-xs text-content-muted">
            {r.incremental ? t('troLyAiKhoSync.tangDan') : t('troLyAiKhoSync.chepDayDu')}
          </p>
        )}
      </div>
    ),
  };

  const cotLanChay: NonNullable<TableProps<ReplicaTableState>['columns']>[number] = {
    title: t('troLyAiKhoSync.colLanChay'),
    dataIndex: 'lastStatus',
    render: (_v, r) => (
      <div className="min-w-0">
        <span className="inline-flex flex-wrap gap-1">
          {r.table === bangDangChay ? <Badge variant="primary">{t('troLyAiKhoSync.trangThaiDangChay')}</Badge> : null}
          <Badge variant={bienTheLanChay(r.lastStatus)}>{nhanTrangThai(r.lastStatus)}</Badge>
        </span>
        {/* Không cắt: câu này là lời giải thích kèm việc phải làm tiếp, cắt một dòng là mất đúng nửa sau. */}
        {r.lastMessage ? (
          <p className="mt-1 text-xs leading-relaxed text-content-secondary">{r.lastMessage}</p>
        ) : null}
      </div>
    ),
  };

  const coThaoTac = (dongBoDuoc && !trucTiep) || xoaDuoc;
  const cotThaoTac: NonNullable<TableProps<ReplicaTableState>['columns']>[number] = {
    title: t('chung.thaoTac'),
    key: 'thaoTac',
    width: 72,
    align: 'center',
    render: (_v, r) => (
      <MenuThaoTac
        tenBanGhi={r.table}
        muc={[
          {
            khoa: 'dong-bo',
            nhan: t('troLyAiKhoSync.dongBoLaiBang'),
            icon: <SyncOutlined />,
            hien: dongBoDuoc && !trucTiep,
            voHieu: dangCoChuKy,
            moTa: dangCoChuKy
              ? t('troLyAiKhoSync.goiYDongBoBangDangBan')
              : t('troLyAiKhoSync.goiYDongBoBang', { bang: r.table }),
            onChon: () => chayBang(r.table),
          },
          {
            khoa: 'xoa',
            nhan: t('troLyAiKhoSync.xoaBang'),
            icon: <DeleteOutlined />,
            hien: xoaDuoc,
            nhom: 'pha-huy',
            onChon: () => setXoaBang(r.table),
          },
        ]}
      />
    ),
  };

  const cot: TableProps<ReplicaTableState>['columns'] = trucTiep
    ? [
        cotTen,
        {
          title: t('troLyAiKhoSync.colSoDongDo'),
          key: 'soDongDo',
          width: 180,
          align: 'end',
          render: (_v, r) => {
            const h = hoSoTheoBang.get(r.table);
            return h ? (
              <span className="inline-flex items-center gap-1.5">
                {h.trenMau ? <Badge variant="neutral">{t('troLyAiKhoSync.uocLuong')}</Badge> : null}
                <span className="tabular-nums text-content-primary">{soVN(h.soDong, locale)}</span>
              </span>
            ) : (
              <span className="text-xs text-content-muted">{t('troLyAiKhoSync.chuaDo')}</span>
            );
          },
        },
        {
          title: t('troLyAiKhoSync.colCotDaDo'),
          key: 'cotDaDo',
          width: 110,
          align: 'end',
          render: (_v, r) => {
            const so = hoSoTheoBang.get(r.table)?.soCot;
            return <span className="tabular-nums text-content-secondary">{so === undefined ? '—' : soVN(so, locale)}</span>;
          },
        },
        ...(coThaoTac ? [cotThaoTac] : []),
      ]
    : [
        cotTen,
        {
          title: t('troLyAiKhoSync.colSoDong'),
          dataIndex: 'rowCount',
          width: 110,
          align: 'end',
          render: (_v, r) => <span className="tabular-nums text-content-primary">{soVN(r.rowCount, locale)}</span>,
        },
        {
          // Cột quan trọng nhất: bản sao nào cũng cũ, và chỉ mốc đồng bộ trả lời được "cũ bao nhiêu".
          title: t('troLyAiKhoSync.colCapNhatDen'),
          dataIndex: 'syncedAt',
          width: 180,
          render: (_v, r) =>
            r.syncedAt ? (
              <span className="text-xs tabular-nums text-content-secondary">{ngayGioVN(r.syncedAt)}</span>
            ) : (
              <span className="text-xs text-content-muted">{t('troLyAiKhoSync.chuaDongBo')}</span>
            ),
        },
        cotLanChay,
        ...(coThaoTac ? [cotThaoTac] : []),
      ];

  const bang = status.data?.tables ?? [];
  const tongDong = bang.reduce((a, b) => a + (b.rowCount ?? 0), 0);
  const soTangDan = bang.filter((b) => b.incremental).length;
  // Mốc CŨ NHẤT: người vận hành cần biết bản sao đang cũ tới đâu ở chỗ cũ nhất.
  const mocCu = bang
    .map((b) => b.syncedAt)
    .filter((x): x is string => !!x)
    .sort()[0];

  return (
    <Bento>
      {/* Bốn con số trả lời "nói chung đang ra sao" trước khi đi vào bảng. */}
      <StatTile
        nhan={t('troLyAiKhoSync.oBang')}
        so={soVN(bang.length, locale)}
        sub={t('troLyAiKhoSync.oBangSub')}
        tone="primary"
        icon={<TableOutlined />}
      />
      {trucTiep ? (
        <>
          <StatTile
            nhan={t('troLyAiKhoSync.oCheDo')}
            so={t('troLyAiKhoSync.oCheDoTrucTiep')}
            sub={t('troLyAiKhoSync.oCheDoTrucTiepSub')}
            tone="success"
            icon={<DatabaseOutlined />}
          />
          <StatTile
            nhan={t('troLyAiKhoSync.oDoLuc')}
            so={
              <span className="text-sm">
                {hoSo.data?.profiledAt ? ngayGioVN(hoSo.data.profiledAt) : t('troLyAiKhoSync.chuaDo')}
              </span>
            }
            sub={t('troLyAiKhoSync.oDoLucSub')}
            tone={hoSo.data?.profiledAt ? 'teal' : 'neutral'}
            icon={<ClockCircleOutlined />}
          />
        </>
      ) : (
        <>
          <StatTile
            nhan={t('troLyAiKhoSync.oDong')}
            so={soVN(tongDong, locale)}
            sub={t('troLyAiKhoSync.oDongSub')}
            tone="teal"
            icon={<DatabaseOutlined />}
          />
          <StatTile
            nhan={t('troLyAiKhoSync.oTangDan')}
            so={soVN(soTangDan, locale)}
            sub={t('troLyAiKhoSync.oTangDanSub', { n: soVN(bang.length - soTangDan, locale) })}
            tone="violet"
            icon={<SyncOutlined />}
          />
          <StatTile
            nhan={t('troLyAiKhoSync.oMoiNhat')}
            so={<span className="text-sm">{mocCu ? ngayGioVN(mocCu) : t('troLyAiKhoSync.chuaCo')}</span>}
            tone={mocCu ? 'success' : 'neutral'}
            icon={<ClockCircleOutlined />}
          />
        </>
      )}

      <Tile>
        <TileHeader title={t('troLyAiKhoSync.bangTieuDe')} icon={<TableOutlined />} tone="teal" />
        <DaiTienDo tienDo={tienDo} />
        <DataTable<ReplicaTableState>
          caption={t('troLyAiKhoSync.bangTieuDe')}
          columns={cot}
          dataSource={bang}
          rowKey="table"
          loading={status.isPending}
          size="small"
          pagination={false}
          locale={{
            // Dòng trống rẽ theo LOẠI NGUỒN: câu của luồng GraphQL trỏ tới một script không liên quan gì tới nguồn DSN.
            emptyText: status.error
              ? moTaLoi(status.error)
              : laDsn
                ? t('troLyAiKhoSync.rongCoNguon')
                : t('troLyAiKhoSync.rongKhongNguon'),
          }}
        />
      </Tile>

      {outcomes ? (
        <Tile span={findings ? 'lg:col-span-6' : 'lg:col-span-12'}>
          <TileHeader title={t('troLyAiKhoSync.chuKyVuaChay')} icon={<SyncOutlined />} tone="success" />
          <TileBody>
            <ul className="space-y-1.5 text-sm">
              {outcomes.map((o) => (
                <li key={o.table} className="flex flex-wrap items-start gap-2">
                  {o.status === 'ok' ? (
                    <CheckCircleOutlined aria-hidden className="mt-1 shrink-0 text-success-700" />
                  ) : (
                    <WarningOutlined aria-hidden className="mt-1 shrink-0 text-warning-700" />
                  )}
                  <span className="font-medium text-content-primary">{o.table}</span>
                  <span className="min-w-0 text-content-secondary">
                    {o.status === 'ok'
                      ? t('troLyAiKhoSync.thongKeChuKy', {
                          cheDo: t(o.stats.mode === 'full' ? 'troLyAiKhoSync.cheDoDayDu' : 'troLyAiKhoSync.cheDoPhanMoi'),
                          doc: soVN(o.stats.rowsRead, locale),
                          ghi: soVN(o.stats.rowsWritten, locale),
                          xoa: soVN(o.stats.rowsDeleted, locale),
                          // Giây, không phải mili-giây: một lượt chép đo bằng phút.
                          giay: soVN(Math.round(o.stats.elapsedMs / 100) / 10, locale),
                        })
                      : o.message}
                  </span>
                </li>
              ))}
            </ul>
          </TileBody>
        </Tile>
      ) : null}

      {findings ? (
        <Tile span={outcomes ? 'lg:col-span-6' : 'lg:col-span-12'}>
          <TileHeader
            title={t('troLyAiKhoSync.boDoThayGi', { n: soVN(findings.length, locale) })}
            icon={<SearchOutlined />}
            tone="violet"
          />
          <TileBody>
            {/* Không có nút "đồng ý tất": một quan hệ bịa đi thẳng vào câu JOIN và cho ra một con số CÓ THẬT trả lời SAI. */}
            <p className="mb-3 text-xs leading-relaxed text-content-secondary">{t('troLyAiKhoSync.boDoGhi')}</p>
            {findings.length === 0 ? (
              <p className="text-sm text-content-muted">{t('troLyAiKhoSync.boDoRong')}</p>
            ) : (
              <div className="space-y-4">
                {NHOM_DE_XUAT.map(({ ma, kinds }) => {
                  const cua = findings.filter((f) => (kinds as readonly string[]).includes(f.kind));
                  if (!cua.length) return null;
                  return (
                    <section key={ma} aria-label={t(`troLyAiKhoSyncNhom.${ma}.ten`, { n: soVN(cua.length, locale) })}>
                      <h4 className="text-xs font-semibold text-content-muted">
                        {t(`troLyAiKhoSyncNhom.${ma}.ten`, { n: soVN(cua.length, locale) })}
                      </h4>
                      <p className="mb-2 mt-0.5 text-xs leading-relaxed text-content-secondary">
                        {t(`troLyAiKhoSyncNhom.${ma}.mo`)}
                      </p>
                      <ul className="space-y-2">
                        {cua.map((f) => {
                          const k = khoaDeXuat(f);
                          const xong = daGhi.has(k);
                          const chac = MUC_CHAC[mucChac(f.confidence)];
                          return (
                            <li
                              key={k}
                              className="flex flex-wrap items-start justify-between gap-3 rounded-lg bg-subtle p-2.5"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="break-all text-sm font-medium text-content-primary">
                                    {f.table}
                                    {f.column ? `.${f.column}` : ''}
                                  </span>
                                  <Badge variant={chac.variant}>{t(chac.khoa)}</Badge>
                                </div>
                                <p className="mt-0.5 text-xs leading-relaxed text-content-secondary">{f.detail}</p>
                              </div>
                              {dongBoDuoc ? (
                                <Button
                                  variant={xong ? 'ghost' : 'secondary'}
                                  size="sm"
                                  disabled={xong}
                                  loading={dangGhi === f}
                                  onClick={() => onGhi(f)}
                                >
                                  {xong ? t('troLyAiKhoSync.daGhi') : t('troLyAiKhoSync.ghiVaoBanKhai')}
                                </Button>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  );
                })}
              </div>
            )}
          </TileBody>
        </Tile>
      ) : null}

      {/* Việc này DROP bảng ở kho bản sao và không lùi được — câu hỏi phải nói ra đúng cái mất đi. */}
      <HopXacNhan
        open={xoaBang !== null}
        onClose={() => {
          setXoaBang(null);
          xoa.reset();
        }}
        tieuDe={t('troLyAiKhoSync.xacNhanXoaBang', { bang: xoaBang ?? '' })}
        than={t('troLyAiKhoSync.xacNhanXoaBangGhi')}
        nhanXacNhan={t('troLyAiKhoSync.xoaBang')}
        dangChay={xoa.isPending}
        loi={xoa.error}
        onXacNhan={() => xoaBang && xacNhanXoa(xoaBang)}
      />
    </Bento>
  );
}
